import * as React from "react";
import { Component } from "react";
import Viewer from "../../component/viewer/Viewer";
import Observations from "../../component/observation/Observations";
import StudyApi, { Negative, NegativeRegion, Observation, Region, RegionType, Study } from "../../api/StudyApi";
import { RouteComponentProps, withRouter } from "react-router-dom";
import EventBus, { Event, Events } from "../../service/EventBus";
import Sentence from "../../component/nlp/Sentence";
import Moment from "react-moment";
import GlmId from "../../component/GlmId";
import { default as TypedReportApi, TypedReport } from "../../api/TypedReportApi";
import { AuthManager } from "../../component/auth/AuthContext";
import "./study-detail.scss";
import LetterBadgeManager, { ALPHABET, LetterBadge } from "../../service/LetterBadgeManager";
import arr from "../../util/ArrayDecorator";
import MessageService from "../../service/MessageService";
import { isNullOrUndefined } from "../../util/LangUtil";
import Tags from "../../component/Tags";
import { AnnotationToolMode } from "../../component/viewer/Viewport";
import EvalStudyAdminApi, { AdminEvalPass, AdminEvalStudy, EvalPassResult } from "../../api/EvalStudyAdminApi";
import { isRectangleTooSmall } from "../../util/RectangleUtility";
import { DisplayMode } from "../../component/viewer/DisplayMode";
import { noStudyResult } from "../../component/viewer/AiDisplayPreferences";
import { HARD_NEGATIVE_CONFIDENCE, NEGATIVE_CONFIDENCE, POSITIVE_CONFIDENCE } from "../../component/viewer/RegionMenu";
import i18n from "i18next";
import ViewportImage from "../../component/viewer/ViewportImage";
import { AdminEvalStudiesPanel, getAuthors } from "./AdminEvalStudiesPanel";

interface StudyDetailProps extends RouteComponentProps<any> {
  authManager: AuthManager;
}

interface StudyDetailState {
  selectedObsIdx: number;
  processCount: number;
  study?: Study;
  viewportImages: Array<ViewportImage>;
  typedReports: TypedReport[];
  comment: string;
  evalStudies?: AdminEvalStudy[];
  availableStudyTags: string[];
  passAuthorIdsToDisplay: string[];
}

const STUDY_TAGS = [
  "E1_HAND",
  "E1_ARM",
  "E1_SHOULDER",
  "E1_FOOT",
  "E1_LEG",
  "E1_PELVIS",
  "E1_SPINE",
  "E1_RIB",
  "NEG",
  "OBV",
  "NON_OBV",
  "OLD",
  "MULTIPLE",
  "ANO",
  "MORPHO",
  "QUAL",
  "SIT",
  "NORMAL",
  "PATIENT_INFO",
];

const ADMIN_IMAGE_TAGS = ["FRONT", "LATERAL", "OTHER"];

class StudyDetail extends Component<StudyDetailProps, StudyDetailState> {
  private subIds: Array<string> = [];
  private studyApi: StudyApi;
  private typedReportApi: TypedReportApi;
  private observationTagService: LetterBadgeManager;
  private evalStudyApi: EvalStudyAdminApi;

  constructor(props: StudyDetailProps) {
    super(props);

    this.studyApi = StudyApi.with(props.authManager.cancelableClient());
    this.typedReportApi = TypedReportApi.with(props.authManager.cancelableClient());
    this.evalStudyApi = EvalStudyAdminApi.with(props.authManager.cancelableClient());
    this.observationTagService = new LetterBadgeManager();

    this.state = {
      selectedObsIdx: -1,
      processCount: 0,
      study: null,
      viewportImages: [],
      typedReports: [],
      comment: "",
      availableStudyTags: STUDY_TAGS,
      passAuthorIdsToDisplay: [],
    };
  }

  UNSAFE_componentWillReceiveProps(nextProp: StudyDetailProps) {
    if (this.props.match.params.id != nextProp.match.params.id) {
      this.processSafely(this.studyApi.getStudy(nextProp.match.params.id), this.resetStudy.bind(this));
    }
  }

  componentDidMount() {
    this.subIds.push(EventBus.subscribe(Events.obs_showregion, this.onObsShowRegion.bind(this)));

    this.processSafely(this.studyApi.getStudy(this.props.match.params.id), this.resetStudy.bind(this));
  }

  componentWillUnmount() {
    this.studyApi.cancel();
    this.typedReportApi.cancel();
    this.evalStudyApi.cancel();
    for (const subId of this.subIds) {
      EventBus.unsubscribe(subId);
    }
  }

  processSafely<T>(promise: Promise<T>, onNext: (item: T) => void) {
    this.setState(
      (prevState) => {
        return { processCount: prevState.processCount + 1 };
      },
      () => {
        const decCounter = () => {
          this.setState((prevState) => {
            return { processCount: prevState.processCount - 1 };
          });
        };

        return promise
          .then(onNext)
          .then(decCounter)
          .catch((err) => {
            decCounter();
          });
      }
    );
  }

  onObsSelected(idx: number) {
    this.state.viewportImages.forEach((img) => {
      img.resetAttenuation();
      img.attenuateOtherRegions(idx);
    });

    this.setState({ selectedObsIdx: idx });
  }

  onObsShowRegion(event: Event) {
    const obs = event.target;

    obs.showRegion = true;
    this.forceUpdate();
  }

  unselectObservation(): void {
    this.setState({ selectedObsIdx: -1 });
  }

  resetStudy(study: Study) {
    this.unselectObservation();

    this.typedReportApi.findByStudyId(study.id).then((typedReports) => this.setState({ typedReports }));

    this.evalStudyApi.findByStudyId(study.id).then((evalStudies) => {
      this.refreshStudy(study, evalStudies, [], evalStudies.flatMap(getAuthors));
    });
  }

  refreshStudy(
    study: Study,
    evalStudies: AdminEvalStudy[] = this.state.evalStudies,
    prevImages = this.state.viewportImages,
    passAuthorIdsToDisplay: string[] = []
  ) {
    this.observationTagService.reset();
    this.assignObsTags(study);

    const viewportImages = ViewportImage.fromStudy(study, prevImages);

    const updatedEvalStudies = evalStudies.map((evalStudy) => {
      return {
        ...evalStudy,
        passes: evalStudy.passes.map((pass, passIdx) => {
          return {
            ...pass,
            _passIdx: passIdx,
            _passColor: ViewportImage.getPassColor(passIdx),
            observations: pass.observations.map((obs, idx) => ({
              ...obs,
              _badge: { label: ALPHABET[idx], color: pass._passColor },
            })),
            _result: this.findResult(pass, evalStudy),
          };
        }),
      };
    });

    updatedEvalStudies.forEach((evalStudy) => {
      const passesToDisplay = evalStudy.passes.filter((pass) => {
        if (passAuthorIdsToDisplay.length > 0) {
          return passAuthorIdsToDisplay.indexOf(pass.author) >= 0;
        }
        return true;
      });

      ViewportImage.addPassesToImage(passesToDisplay, viewportImages);
    });

    // Display first image by default
    const selectedIdx = arr(viewportImages).findFirstIndex((imgElt) => imgElt.selected);
    if (selectedIdx < 0 && viewportImages.length > 0) viewportImages[0].selected = true;

    // Select first obs by default
    let selectedObsIdx = this.state.selectedObsIdx;
    if (selectedObsIdx < 0 && study.observations.length + study.negatives.length > 0) {
      selectedObsIdx = 0;
    }

    this.setState({
      study,
      evalStudies: updatedEvalStudies,
      comment: study.comment || "",
      viewportImages: viewportImages,
      selectedObsIdx,
      availableStudyTags: this.computeAvailableTags(study.tags),
      passAuthorIdsToDisplay: passAuthorIdsToDisplay,
    });
  }

  removeObservation(observation: Observation) {
    this.unselectObservation();
    const study = this.state.study;
    arr(study.observations).remove((obs) => obs.id == observation.id);
    this.refreshStudy(study);
  }

  createObservation(type: string): Promise<Observation> {
    return this.studyApi.newObservation(this.state.study, type).then((obs) => {
      this.setState((prevState) => {
        const study = prevState.study;
        study.observations.push(obs);
        this.assignObsTags(study);
        return { study };
      });
      return obs;
    });
  }

  createNegative(type: string): Promise<Negative> {
    const negative: Negative = {
      type: type,
      regions: [],
    };

    return new Promise((resolve, reject) => {
      if (arr(this.state.study.negatives).contains((n) => n.type === type)) {
        const msg = i18n.t("error.negativeExists", { type: type });
        MessageService.error(msg);
        reject(msg);
        return;
      }

      this.setState(
        (prevState) => {
          prevState.study.negatives.push(negative);
          return prevState;
        },
        () => {
          resolve(negative);
        }
      );
    });
  }

  saveRegion(region: Region, fromAiCopy = false) {
    if (isRectangleTooSmall(region)) {
      MessageService.error(i18n.t("error.boxTooSmall"));
      this.refreshStudy(this.state.study);
      return;
    }

    if (this.state.selectedObsIdx < 0) {
      MessageService.error(i18n.t("error.noObs"));
      // refresh study to avoid unsaved region stays on the viewports
      this.refreshStudy(this.state.study);
      return;
    }

    // Creation time
    if (isNullOrUndefined(region.obsIdx)) {
      region.obsIdx = this.state.selectedObsIdx;
    }

    if (isNullOrUndefined(region._type)) {
      if (this.state.selectedObsIdx < this.state.study.observations.length) {
        region._type = RegionType.OBSERVATION;
      } else {
        region._type = RegionType.NEGATIVE;
      }
    }

    // Default confidence value
    if (isNullOrUndefined(region.confidence)) {
      if (region._type === RegionType.NEGATIVE) {
        if (fromAiCopy) {
          region.confidence = HARD_NEGATIVE_CONFIDENCE;
        } else {
          region.confidence = NEGATIVE_CONFIDENCE;
        }
      } else {
        region.confidence = POSITIVE_CONFIDENCE;
      }
    }

    const obsArrayLength = this.state.study.observations.length;
    let regions: Region[];

    if (region._type === RegionType.OBSERVATION && region.obsIdx < obsArrayLength) {
      const obs = this.state.study.observations[region.obsIdx];
      regions = obs.regions;
    } else if (region._type === RegionType.NEGATIVE && region.obsIdx >= obsArrayLength) {
      const neg = this.state.study.negatives[region.obsIdx - this.state.study.observations.length];
      regions = neg.regions;
    } else {
      throw i18n.t("error.obsNotMatch", { type: region._type });
    }

    const regionIdx = regions.indexOf(region);
    if (regionIdx < 0) {
      regions.push(region);
    }

    this.refreshStudy(this.state.study);
  }

  deleteRegion(region: Region) {
    if (region && region.obsIdx > -1) {
      const study = this.state.study;

      if (region._type === RegionType.OBSERVATION) {
        const obs = study.observations[region.obsIdx];
        arr(obs.regions).remove((r) => r == region);
      } else if (region._type === RegionType.NEGATIVE) {
        const neg = study.negatives[region.obsIdx - study.observations.length];
        arr(neg.regions).remove((r) => r === region);
      } else {
        throw i18n.t("error.unknownRegionType", { type: region._type });
      }

      this.refreshStudy(study);
    }
  }

  onClose(evt: React.MouseEvent<HTMLButtonElement>) {
    evt.preventDefault();

    EventBus.publish(Events.study_finished, this.state.study);

    this.props.history.push("/study");
  }

  nextStudy(evt: React.MouseEvent<HTMLButtonElement>) {
    evt.preventDefault();
    EventBus.publish(Events.study_finished, this.state.study);
    EventBus.publish(Events.study_next, this.state.study);
  }

  // ########################################## UI/UX

  render() {
    if (this.state.study) {
      return (
        <div className="study-detail">
          {this.state.processCount > 0 && (
            <div className="fullscreen-spinner">
              <i className="fa fa-spinner fa-spin" />
            </div>
          )}

          <Viewer
            study={this.state.study}
            observations={this.state.study.observations}
            images={this.state.viewportImages}
            saveRegion={(r, fromCopy) => this.saveRegion(r, fromCopy)}
            deleteRegion={(r) => this.deleteRegion(r)}
            addAIRegionToNegative={(r) => this.addAIRegionToNegative(r)}
            authManager={this.props.authManager}
            onImageTagsChanged={(imageId, tags) => this.setImageTagsById(imageId, tags)}
            imageTags={this.getImageTags()}
            aiDisplayPreferences={noStudyResult()}
            display={DisplayMode.studyDetail()}
            annotationToolMode={AnnotationToolMode.REGION}
            actions={() => (
              <div className="btn-group">
                <button
                  className="btn btn-primary px-2"
                  title={i18n.t("global.save")}
                  onClick={(evt) => this.save(evt)}
                >
                  <i className="fa fa-save" />
                </button>

                <button
                  className="btn btn-secondary px-2"
                  title={i18n.t("global.next")}
                  onClick={(evt) => this.nextStudy(evt)}
                >
                  <i className="fa fa-arrow-right" />
                </button>
                <button
                  className="btn btn-secondary px-2"
                  title={i18n.t("global.close")}
                  onClick={(evt) => this.onClose(evt)}
                >
                  <i className="fa fa-close" />
                </button>
              </div>
            )}
            aside={() => (
              <div>
                <div>
                  <div className="card">
                    <div className="card-title">Tags</div>
                    <div className="card-body">
                      <Tags
                        tags={this.state.study.tags}
                        availableTags={this.state.availableStudyTags}
                        onTagsChanged={this.onTagsChanged}
                      />
                    </div>
                  </div>

                  <Observations
                    study={this.state.study}
                    selectedIdx={this.state.selectedObsIdx}
                    onSelect={(idx) => this.onObsSelected(idx)}
                    getLetterBadge={(obsIdx) => this.getLetterBadge(obsIdx)}
                    removeObservation={(obs) => this.removeObservation(obs)}
                    createObservation={(type) => this.createObservation(type)}
                    createNegative={(type) => this.createNegative(type)}
                    removeNegative={(negative) => this.removeNegative(negative)}
                    authManager={this.props.authManager}
                  />

                  <div className="card">
                    <div className="card-title">{i18n.t("global.typedReport")}</div>
                    <div className="card-body">
                      {this.state.typedReports &&
                        this.state.typedReports.map((report) => (
                          <div key={report.id}>
                            {report.sentences &&
                              report.sentences.map((sentence, si) => (
                                <Sentence key={si} value={sentence} displayTkType={true} />
                              ))}
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-title">{i18n.t("global.comment")}</div>
                    <div className="card-body">
                      <textarea
                        className="form-control"
                        style={{ width: "100%" }}
                        rows={5}
                        value={this.state.study.comment || ""}
                        onChange={(evt) => this.setStudyComment(evt)}
                      />
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-title">Cas</div>
                  <div className="card-body">
                    StudyId : <GlmId value={this.state.study.id} /> <br />
                    {i18n.t("global.age")} : {this.state.study.patientAge} | {i18n.t("global.sex")}:{" "}
                    {this.state.study.patientSex}
                    <br />
                    {i18n.t("study.indication")} : {this.state.study.indication}
                    <br />
                    {i18n.t("dicom.studyDate")} :{" "}
                    <Moment format="DD/MM/YYYY HH:mm">{this.state.study.studyDate}</Moment>
                    <br />
                    {i18n.t("dicom.studyDescription")} : {this.state.study.studyDescription} <br />
                  </div>
                </div>

                <AdminEvalStudiesPanel
                  evalStudies={this.state.evalStudies}
                  authorIdsToDisplay={this.state.passAuthorIdsToDisplay}
                  onAuthorsChanged={this.onPassAuthorsToDisplayChanged}
                />
              </div>
            )}
          />
        </div>
      );
    } else {
      return <div>{i18n.t("global.loading")}</div>;
    }
  }

  private assignObsTags(study: Study) {
    study.observations.forEach((obs, idx) => {
      obs._badge = this.observationTagService.getLetterBadge(obs.type, String(idx));
    });
  }

  private removeNegative(negative: Negative) {
    this.unselectObservation();
    const study = this.state.study;
    arr(study.negatives).remove((neg) => neg == negative);
    this.refreshStudy(study);
  }

  private save(evt: React.MouseEvent<HTMLButtonElement>) {
    evt.preventDefault();

    this.processSafely(this.studyApi.saveStudy(this.state.study), (updatedStudy: Study) => {
      this.refreshStudy(updatedStudy);
      EventBus.publish(Events.study_finished, updatedStudy);
    });
  }

  private getLetterBadge(obsIdx: number): LetterBadge {
    return this.state.study.observations[obsIdx]._badge;
  }

  private setImageTagsById(imageId: string, tags: Array<string>) {
    const study = this.state.study;
    study.series.forEach((s) =>
      s.images.forEach((img) => {
        if (img.id === imageId) {
          img.tags = tags;
        }
      })
    );
    this.refreshStudy(study);
  }

  private setStudyComment(evt: React.ChangeEvent<HTMLTextAreaElement>) {
    this.state.study.comment = evt.target.value;
    this.setState({ study: this.state.study });
  }

  private onTagsChanged = (newTags: Array<string>) => {
    this.setState((prevState) => ({
      study: { ...prevState.study, tags: newTags },
    }));
  };

  private onPassAuthorsToDisplayChanged = (authorIdsToDisplay: string[]) => {
    this.refreshStudy(this.state.study, this.state.evalStudies, this.state.viewportImages, authorIdsToDisplay);
  };

  private addAIRegionToNegative(region: Region) {
    let negative = this.state.study.negatives.find((negative) => negative.type === region.obsType);

    const negRegion: NegativeRegion = {
      ...region,
      tags: [],
      confidence: HARD_NEGATIVE_CONFIDENCE,
    };

    const { study: currentStudy } = this.state;
    const updatedStudy = {
      ...currentStudy,
      negatives: [...currentStudy.negatives],
    };

    if (!negative) {
      negative = {
        type: region.obsType,
        regions: [],
      };

      updatedStudy.negatives.push(negative);
    }

    negative.regions.push(negRegion);

    this.refreshStudy(updatedStudy);
  }

  private getImageTags() {
    return Array.from(
      new Set(this.state.study.series.flatMap((s) => s.images.flatMap((i) => i.tags)).concat(ADMIN_IMAGE_TAGS))
    ).sort();
  }

  private computeAvailableTags(studyTags: string[]) {
    return Array.from(new Set([...STUDY_TAGS, ...studyTags]));
  }

  private findResult(pass: AdminEvalPass, evalStudy: AdminEvalStudy): EvalPassResult {
    return arr(evalStudy.results).findFirst((r) => r.author == pass.author && r.workflow === pass.workflow);
  }
}

export default withRouter(StudyDetail);
